import React, { useRef, useState } from "react";
import { ArrowRight, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import SectionHeading from "./SectionHeading";
import { DifferentialDialog, DIFFERENTIAL_ICONS } from "./why/DifferentialDialog";
import "./why/why.css";

const PAGE_SIZE = 6;

const WhySection = () => {
  const { lang } = useLanguage();
  const t = content[lang].why;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [page, setPage] = useState(0);
  const openerRef = useRef(null);

  const pages = [];
  for (let i = 0; i < t.items.length; i += PAGE_SIZE) pages.push(t.items.slice(i, i + PAGE_SIZE));
  const totalPages = pages.length;
  const goPage = (dir) => setPage((p) => (p + dir + totalPages) % totalPages);

  const openDetail = (index, event) => {
    openerRef.current = event.currentTarget;
    setActiveIndex(index);
    setOpen(true);
  };

  const restoreFocus = (event) => {
    event.preventDefault();
    openerRef.current?.focus({ preventScroll: true });
  };

  return (
    <section id="por-que-narwhal" className="why-section relative py-24 md:py-28 scroll-mt-20" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div data-testid="why-heading">
          <SectionHeading kicker={t.kicker} title={t.title} accent={t.titleAccent} />
        </div>

        <div className="relative mt-12" data-testid="why-differentials-viewport">
          {totalPages > 1 && (
            <>
              <button
                type="button"
                onClick={() => goPage(-1)}
                aria-label="Anteriores diferenciales"
                data-testid="why-prev-button"
                className="absolute -left-1 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-narwhal-dark/70 text-white/70 backdrop-blur transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal md:-left-5"
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => goPage(1)}
                aria-label="Siguientes diferenciales"
                data-testid="why-next-button"
                className="absolute -right-1 top-1/2 z-20 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/15 bg-narwhal-dark/70 text-white/70 backdrop-blur transition-colors duration-200 hover:border-narwhal-teal/50 hover:text-narwhal-teal md:-right-5"
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </>
          )}

          <div className="overflow-hidden md:mx-8">
            <div
              className="flex"
              style={{ transform: `translateX(-${page * 100}%)`, transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)" }}
            >
              {pages.map((chunk, pageIndex) => (
                <div key={pageIndex} className="grid w-full shrink-0 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`why-page-${pageIndex}`}>
                  {chunk.map((item, idx) => {
                    const i = pageIndex * PAGE_SIZE + idx;
                    const Icon = DIFFERENTIAL_ICONS[i];
                    return (
                      <article key={i} className="why-card" data-testid={`why-card-${i}`}>
                        <h3 data-testid={`why-card-heading-${i}`}>
                          <button type="button" className="why-card-open" aria-haspopup="dialog" onClick={(event) => openDetail(i, event)} data-testid={`why-card-open-${i}`}>
                            <span className="why-card-icon" data-testid={`why-card-icon-${i}`} aria-hidden="true">{Icon && <Icon />}</span>
                            <span className="why-card-title" data-testid={`why-card-title-${i}`}>{item.title}</span>
                            <Plus className="why-card-plus" aria-hidden="true" />
                          </button>
                        </h3>
                        <p className="why-card-summary" data-testid={`why-card-summary-${i}`}>{item.text}</p>
                      </article>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2" data-testid="why-dots">
              {pages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  aria-label={`Ir a la página ${i + 1}`}
                  data-testid={`why-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === page ? "w-6 bg-narwhal-teal" : "w-2 bg-white/20 hover:bg-white/40"}`}
                />
              ))}
            </div>
          )}
        </div>

        <button type="button" className="why-explore mt-8 inline-flex items-center gap-2 text-sm text-narwhal-teal" aria-haspopup="dialog" onClick={(event) => openDetail(0, event)} data-testid="why-explore-button">
          {t.link}<ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
      <DifferentialDialog t={t} open={open} onOpenChange={setOpen} activeIndex={activeIndex} onSelect={setActiveIndex} onCloseAutoFocus={restoreFocus} />
    </section>
  );
};

export default WhySection;
